import { Select } from './Select';

/**
 * Ordering and grouping for a list.
 *
 * Two screens want the same three controls -- what to order by, which way, and
 * how to group -- and a second copy would drift the moment one of them grew a
 * fourth. The options themselves stay with the screen, because what a fleet
 * sorts by and what a day's work sorts by are not the same vocabulary.
 */

export interface ListOption<T extends string> {
  value: T;
  label: string;
}

export function ListControls<S extends string, G extends string, F extends string = never>({
  sorts,
  sort,
  onSort,
  ascending,
  onDirection,
  groups,
  group,
  onGroup,
  filters,
  filter,
  onFilter,
}: {
  sorts: ListOption<S>[];
  sort: S;
  onSort: (next: S) => void;
  ascending: boolean;
  onDirection: (ascending: boolean) => void;
  groups: ListOption<G>[];
  group: G;
  onGroup: (next: G) => void;
  /**
   * What the list is narrowed to, where a screen has such a thing.
   *
   * Ahead of the sort, because it changes what is in the list rather than the
   * order of it -- and a reader needs to know they are looking at a subset
   * before they interpret what they see. Grouping is the control it is most
   * easily confused with and the difference matters: a group puts the rows you
   * do not want at the bottom, a filter takes them out.
   */
  filters?: ListOption<F>[];
  filter?: F;
  onFilter?: (next: F) => void;
}) {
  return (
    <div className="list-controls">
      {filters && filter !== undefined && onFilter ? (
        <Select value={filter} options={filters} onChange={(v) => onFilter(v as F)} />
      ) : null}
      <Select value={sort} options={sorts} onChange={(v) => onSort(v as S)} />

      {/*
       * Two buttons rather than one that flips.
       *
       * A single toggle can only ever offer the direction you are not in, so
       * choosing ascending while already ascending is impossible and the arrow
       * has to be decoded before you know what a click will do. Here both
       * directions are always present and always say which one is on.
       */}
      <div className="segmented" role="group" aria-label="Sort direction">
        <button
          type="button"
          className="segment"
          aria-pressed={ascending}
          onClick={() => onDirection(true)}
          title="Ascending"
          aria-label="Sort ascending"
        >
          <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
            <path
              d="M6 9.5V2.5M3 5.5L6 2.5l3 3"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <button
          type="button"
          className="segment"
          aria-pressed={!ascending}
          onClick={() => onDirection(false)}
          title="Descending"
          aria-label="Sort descending"
        >
          <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
            <path
              d="M6 2.5v7M3 6.5l3 3 3-3"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      <Select value={group} options={groups} onChange={(v) => onGroup(v as G)} align="right" />
    </div>
  );
}

/** A heading over one section of a grouped list. */
export function ListGroup({ label, count }: { label: string; count: number }) {
  return (
    <h3 className="list-group">
      {label}
      <span className="count-pill">{count}</span>
    </h3>
  );
}
